import { defineStore } from 'pinia';
import { createStorage, setStorage, removeStorage, getStorage, STORAGE_KEYS } from '@/utils/storage';
import request, { setAxiosHeader, getAxiosHeader } from '@/utils/request';
import * as Sentry from '@sentry/vue';
import { convivaService } from '@/services/conviva/conviva-service';
import useSubscriptionStore from '@/store/subscription';
import useAudioPreferenceStore from '@/store/audio-preference';
interface UserInfo {
  id: string;
  name?: string;
  nickname?: string;
  avatar?: string;
  firebase_id?: string;
  first_name?: string;
  last_name?: string;
  phone?: string;
  email?: string;
}
const userInfoStorage = createStorage<UserInfo | null>(STORAGE_KEYS.USER_INFO, null);
const gsidStorage = createStorage<string | null>(STORAGE_KEYS.GSID, null);

const useUserStore = defineStore('user', {
  state: () => ({
    userInfo: userInfoStorage as UserInfo | null,
    gsid: gsidStorage || null, // 从 cookie 初始化 token
    isFromLogin: createStorage(STORAGE_KEYS.IS_FROM_LOGIN, false) as boolean, // 标识是否来自登录页面，持久化存储
    guestFlag: 0, //访客授权
  }),
  getters: {
    isLogin: state => !!state.gsid && !!state.userInfo && state.userInfo?.id,
  },
  actions: {
    // set sentry user
    setSentryUser(user: UserInfo | null) {
      if (user) {
        Sentry.setUser({
          // Standard Sentry fields
          id: user?.firebase_id || '',
          username: user?.name,
          // Custom fields
          user_id: user?.id ? String(user.id) : '',
          avatar: user?.avatar || '',
          first_name: user?.first_name || '',
          last_name: user?.last_name || '',
        });
      } else {
        Sentry.setUser(null); // clear user on logout
      }
    },
    // set conviva user - use firebase_id, fallback to id, then 'NA'
    setConvivaUser() {
      const userId = this.userInfo?.firebase_id || this.userInfo?.id || 'NA';
      convivaService.setUserId(String(userId));
    },
    // 模拟 API 调用获取用户信息
    async getUserInfo() {
      return this.userInfo;
    },
    // 模拟登录
    async login(userInfo: UserInfo, gsid: string, fromLogin = false) {
      this.userInfo = userInfo;
      setStorage(STORAGE_KEYS.USER_INFO, userInfo);
      this.isFromLogin = fromLogin;
      this.setLoginGsid(gsid);

      // set sentry user
      this.setSentryUser(userInfo);

      // set conviva user
      this.setConvivaUser();

      if (process.env.NODE_ENV !== 'production') {
        const storedUser = getStorage<UserInfo | null>(STORAGE_KEYS.USER_INFO);
        const userId = storedUser?.firebase_id || '';
        console.log('[DEBUG] login persisted state', {
          userId,
          storedUser,
        });
      }
    },
    // 登出
    async logout() {
      if (this.gsid) {
        try {
          await request.post('/sso/api/logout', {
            entry: 'spl',
            gsid: this.gsid,
          });
        } catch (e) {
          // Ignore logout API errors (e.g. expired gsid, "error Params") – we still clear local state
        }
      }
      this.userInfo = null;
      setStorage(STORAGE_KEYS.USER_INFO, null);
      this.clearLoginGsid();
      this.resetFromLogin();
      // clear sentry user
      this.setSentryUser(null);
      this.setConvivaUser();
      // clear subscription cache
      useSubscriptionStore().clear();
      useAudioPreferenceStore().clear();
    },
    setLoginGsid(gsid: string) {
      this.gsid = gsid;
      setStorage(STORAGE_KEYS.GSID, gsid);
      // Cookies.set('gsid', gsid, { expires: 7 });
      setAxiosHeader('gsid', gsid);
    },
    clearLoginGsid() {
      this.gsid = null;
      removeStorage(STORAGE_KEYS.GSID);
      const vgsid = getAxiosHeader('vgsid');
      vgsid && setAxiosHeader('gsid', vgsid);
    },
    // 设置登录来源状态
    setFromLogin(isFromLogin: boolean) {
      console.log('[DEBUG] setFromLogin called:', { isFromLogin, env: process.env.NODE_ENV });
      this.isFromLogin = isFromLogin;
      setStorage(STORAGE_KEYS.IS_FROM_LOGIN, isFromLogin);
    },
    // 重置登录来源状态
    resetFromLogin() {
      console.log('[DEBUG] resetFromLogin called:', { currentState: this.isFromLogin, env: process.env.NODE_ENV });
      this.isFromLogin = false;
      setStorage(STORAGE_KEYS.IS_FROM_LOGIN, false);
    },
    setGuestFlag(flag: number) {
      this.guestFlag = flag;
    },
    resetGuestFlag() {
      this.guestFlag = 0;
    },
    // 清理存储状态 - 防止环境间状态持久化问题
    clearStorageState() {
      console.log('[DEBUG] clearStorageState called - cleaning up login state');
      this.resetFromLogin();
      // 始终清理登录状态，防止跨环境的状态持久化问题
      setStorage(STORAGE_KEYS.IS_FROM_LOGIN, false);
    },
  },
});

export default useUserStore;
